import { z } from 'zod';
import { uuid, isoTimestamp, storeCode, cents } from './common.js';
import { ADAPTER_HEALTH, STORE_CODES } from '../enums.js';
/**
 * Search (API §4). Local and live are separate endpoints because they have
 * completely different latency and availability characteristics.
 */
/**
 * A boolean carried in a query string. `z.coerce.boolean()` is a trap here —
 * it treats every non-empty string as true, so `organic=false` would read as
 * true — so only the literal on-values count and everything else is off.
 */
const boolParam = z
    .string()
    .optional()
    .transform((v) => v === 'true' || v === '1');
export const localSearchQuery = z.object({
    q: z.string().min(1).max(100),
    store: storeCode.optional(),
    /** Return only organic products (migration 019). */
    organic: boolParam,
    /** Float organic products up among otherwise equally ranked results. */
    prefer_organic: boolParam,
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const searchResultDto = z.object({
    generic_product: z.object({ id: uuid, name: z.string(), category: z.string().nullable() }),
    variant: z
        .object({
        id: uuid,
        name: z.string(),
        brand: z.string().nullable(),
        store: storeCode,
        size_label: z.string().nullable(),
    })
        .nullable(),
    shelf_price_cents: cents.nullable(),
    shelf_price_fetched_at: isoTimestamp.nullable(),
    /** A price the user might act on must declare its own freshness (API §4.1). */
    stale: z.boolean(),
    last_paid_cents: cents.nullable(),
    purchase_count: z.number().int(),
    /**
     * Whether this is an organic product (migration 019) — true when the chosen
     * variant is organic, or the generic itself is (a "biologische …" the
     * household entered by hand). Lets the client badge and filter it.
     */
    organic: z.boolean(),
    /** Why this ranked where it did. Not optional (ARCHITECTURE §3.2). */
    reason: z.string().nullable(),
});
export const localSearchResponse = z.object({
    results: z.array(searchResultDto),
});
/**
 * Per-store status, so the UI can say "Ekoplaza unavailable" instead of
 * pretending the store has no oat milk. Partial results are a success.
 */
export const storeStatusDto = z.object({
    ok: z.boolean(),
    count: z.number().int().optional(),
    reason: z.string().optional(),
});
/* ---------------- live search (API §4.2) ---------------- */
/**
 * `stores` is the comma-separated form from the query string — omitted means
 * every store whose adapter currently works, which is what the add-product
 * screen wants.
 */
export const liveSearchQuery = z.object({
    q: z.string().min(1).max(100),
    stores: z
        .string()
        .optional()
        .transform((v) => v
        ? v
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined)
        .pipe(z.array(storeCode).min(1).max(STORE_CODES.length).optional()),
    /**
     * Float organic products up the page (migration 019).
     *
     * A **reorder, never a filter**: every hit still comes back, so this cannot
     * hide a product the way API §4.2 forbids. It only ever breaks a tie —
     * organic wins among rows that relevance and the model both judged equal — so
     * a relevant non-organic product is never pushed below an irrelevant organic
     * one. Filtering to organic only is the client's job, post-hoc, exactly like
     * the store filter.
     */
    prefer_organic: boolParam,
    /**
     * How many rows to **retrieve** per store, which is not how many a screen
     * shows.
     *
     * It was ten, and ten is too few to compare shops with: live, `melk` came
     * back with 42 rows and formed **zero** bundles, because Jumbo's litre carton
     * of Campina and Albert Heijn's were each other's row eleven (SEARCH_GAPS
     * R2). No rule change reaches that — the row was not there.
     *
     * **Thirty is measured, not guessed** (`npm run measure:retrieval`, twelve
     * queries against the four live shops, 9 August 2026). The metric is the one
     * this number actually governs — whether the member a cross-shop bundle needs
     * was on the page at all:
     *
     *     limit 10    453 rows     5 cross-shop bundles    recall  23%
     *     limit 20    881 rows    16 cross-shop bundles    recall  73%
     *     limit 30   1203 rows    22 cross-shop bundles    recall 100%
     *
     * Depth added **seventeen** bundles and not one wrong one — every pair was
     * read: `Doritos Mexican-style taco` met its Jumbo row, `Campina Magere
     * yoghurt` met its litre, `Douwe Egberts Aroma rood` met both its pack sizes.
     * That matters more than the percentages, because the trade this file is bent
     * around is that a wrong bundle costs more than a missed one, and a deeper
     * page turned out not to buy any.
     *
     * Thirty rather than forty because that is where the curve flattens: at 40,
     * `koffie` gains nothing at all and `coca cola` gains one mini eight-pack.
     * The max keeps headroom above the default rather than sitting on it.
     *
     * The upstream cost is small and was the reason ten looked safe. Jumbo, Lidl
     * and Ekoplaza already fetch a full page and slice it locally, so for three
     * of the four shops this is the same request; only Albert Heijn's is bigger,
     * and it is still **one** request, which is what ADAPTERS §4 governs. Raising
     * a page size is not paging through a catalogue — do not turn it into that.
     *
     * The cost that is *not* free is the model's: a first search of a word now
     * asks about ~100 titles rather than ~40. `maxTokensFor` scales the answer
     * budget so that cannot silently truncate, but the call still has 4 seconds
     * (`AI_SEARCH_TIMEOUT_MS`) and a bigger page is likelier to spend them. It
     * degrades the safe way — deterministic order, exactly as if the model were
     * not configured — and verdicts are cached per word *and* product, so it is
     * the first search of a word that pays, not every search.
     */
    limit: z.coerce.number().int().min(1).max(40).default(30),
});
/**
 * One live hit. Not yet persisted — selection is what persists it (API §4.2,
 * §4.4) — so it is keyed by `(store, external_id)` rather than by a row id.
 */
export const liveResultDto = z.object({
    store: storeCode,
    external_id: z.string(),
    name: z.string(),
    brand: z.string().nullable(),
    size_label: z.string().nullable(),
    size_value: z.number().nullable(),
    size_unit: z.string().nullable(),
    /**
     * How many items the pack holds — 4 for "4 x 500 ml", null for a single.
     *
     * `size_value` is the *total*, because that is what a price per litre needs,
     * and totalling is lossy: a four-pack of half-litres and a two-litre bottle
     * are both "2000 ml" and are not the same product. Identity reads this
     * (SEARCH_GAPS I1); nothing about the price does.
     */
    pack_count: z.number().int().positive().nullable(),
    /** AH exposes this only on the detail endpoint, so it is null until selection. */
    ean: z.string().nullable(),
    price_cents: cents.nullable(),
    promo_price_cents: cents.nullable(),
    promo_label: z.string().nullable(),
    /** Null when the size could not be parsed — never guessed (ADAPTERS §9.3). */
    unit_price_cents: cents.nullable(),
    unit: z.string().nullable(),
    /**
     * The store sells this in its shops but not online, so it publishes no price.
     *
     * Not the same fact as `price_cents === null`, which happens for several
     * unrelated reasons — a price that failed the sanity check, a promotion with
     * no stated original, a parse that found nothing. Only this says *the store
     * never printed one*, which is what lets the client write "Alleen in de
     * winkel" instead of leaving a hole where every other shop shows a number.
     *
     * True on Lidl's whole grocery range, priced flyer offers included: those are
     * in-store-only *and* priced, and both halves are worth saying.
     */
    in_store_only: z.boolean(),
    /**
     * Whether this is an organic product (migration 019), detected from the shop,
     * the title and the brand (`shared/src/organic.ts`). Everything from Ekoplaza
     * is organic; elsewhere it is the ones whose name or brand says `bio` /
     * `biologisch`. Lets the client badge organic hits and filter to them.
     */
    organic: z.boolean(),
    /** The shop's own URL. Kept on the wire for diagnosis; the client renders `image_path`. */
    image_url: z.string().nullable(),
    /** Same-origin path this app serves the picture from (migration 015). */
    image_path: z.string().nullable(),
    /**
     * True when this store product is already a variant in the household's
     * catalogue. The client then adds the known variant instead of offering to
     * create a second one.
     */
    in_catalog: z.boolean(),
    variant_id: uuid.nullable(),
    /**
     * What the model made of this title, relative to the word that was typed —
     * `the_thing`, `made_from_it`, `unrelated` — or null when it was not judged
     * (no key, spent ceiling, timeout, or simply not in the cache yet).
     *
     * It is on the wire so the client can *section* the shops' noise rather than
     * silently sorting it to the bottom of one list: two of the four answer an
     * unmatched query with an arbitrary promotional page, and a screen half full
     * of unrelated products reads as a bug in this app (SEARCH_GAPS R1). It is
     * never a licence to drop a row — nothing filters (API §4.2) — and a null
     * means the screen behaves exactly as it did before this field existed.
     */
    relevance: z.enum(['the_thing', 'made_from_it', 'unrelated']).nullable(),
});
export const liveSearchResponse = z.object({
    results: z.array(liveResultDto),
    /** Partial results are a success; absence is never read as "they don't sell it". */
    stores: z.record(storeCode, storeStatusDto),
});
/* ---------------- comparison (API §4.3) ---------------- */
/**
 * One endpoint behind both entry points (ARCHITECTURE §5.5): text from search,
 * a generic product from a list item. Same query, same component — the only
 * difference is the input.
 */
export const compareQuery = z
    .object({
    q: z.string().min(1).max(100).optional(),
    generic_id: uuid.optional(),
    limit: z.coerce.number().int().min(1).max(30).default(10),
})
    .refine((v) => Boolean(v.q) !== Boolean(v.generic_id), {
    message: 'pass exactly one of q or generic_id',
});
export const compareResultDto = z.object({
    variant: z.object({
        /** Null for a live hit the household has never bought — nothing is persisted yet. */
        id: uuid.nullable(),
        name: z.string(),
        brand: z.string().nullable(),
        store: storeCode,
        size_label: z.string().nullable(),
        external_id: z.string().nullable(),
        /**
         * Same-origin path for the product photograph (migration 015).
         *
         * This screen is where a picture earns the most: four shops' names for the
         * same yoghurt differ enough that "is this actually the same product" is
         * the question being asked, and the photograph answers it faster than the
         * name does.
         */
        image_path: z.string().nullable(),
        /** Whether this is an organic product (migration 019). */
        organic: z.boolean(),
    }),
    /** What you would pay today: the promotion when there is one. */
    price_cents: cents.nullable(),
    shelf_price_cents: cents.nullable(),
    promo_label: z.string().nullable(),
    /** Shelf price large, unit price small underneath (ARCHITECTURE §5.5). */
    unit_price_cents: cents.nullable(),
    unit: z.string().nullable(),
    price_fetched_at: isoTimestamp.nullable(),
    stale: z.boolean(),
    /** The household's usual choice, pinned first and marked with its reason. */
    habitual: z.boolean(),
    reason: z.string().nullable(),
    in_catalog: z.boolean(),
});
export const compareResponse = z.object({
    generic_product: z
        .object({ id: uuid, name: z.string(), category: z.string().nullable() })
        .nullable(),
    results: z.array(compareResultDto),
    /** Unavailable stores are listed, so absence is never read as "they don't sell it". */
    stores: z.record(storeCode, storeStatusDto),
});
/* ---------------- selection (API §4.4) ---------------- */
/**
 * Selection is the catalogue growth mechanic (ARCHITECTURE §5.1): the
 * catalogue ends up containing exactly what the household actually uses.
 *
 * The server re-fetches the product from the adapter — that is the only way to
 * get an EAN out of Albert Heijn — but every field is also accepted from the
 * client as a fallback. The client has them from the search result it just
 * rendered, and a store that goes down between searching and tapping must not
 * make the product impossible to add.
 */
export const createVariantRequest = z.object({
    /** Client-generated, so an offline selection already has its final identity. */
    id: uuid.optional(),
    store: storeCode,
    external_id: z.string().min(1).max(200),
    /** Attach to a generic the household already has — from the list or compare view. */
    generic_product_id: uuid.optional(),
    /** Otherwise a generic is created: this id, this name. */
    generic_id: uuid.optional(),
    generic_name: z.string().min(1).max(200).optional(),
    category: z.string().max(100).nullish(),
    /** Fallbacks, used only when the upstream fetch fails. */
    name: z.string().min(1).max(300).optional(),
    brand: z.string().max(200).nullish(),
    size_value: z.number().positive().nullish(),
    size_unit: z.string().max(20).nullish(),
    price_cents: cents.nullish(),
    promo_price_cents: cents.nullish(),
    promo_label: z.string().max(200).nullish(),
    image_url: z.string().max(1000).nullish(),
});
export const variantDto = z.object({
    id: uuid,
    generic_product_id: uuid,
    store: storeCode,
    name: z.string(),
    brand: z.string().nullable(),
    size_value: z.number().nullable(),
    size_unit: z.string().nullable(),
    size_label: z.string().nullable(),
    ean: z.string().nullable(),
    external_id: z.string().nullable(),
    image_url: z.string().nullable(),
    /** Same-origin path this app serves the picture from (migration 015). */
    image_path: z.string().nullable(),
    /** Whether this is an organic product (migration 019). */
    organic: z.boolean(),
    price_cents: cents.nullable(),
    price_fetched_at: isoTimestamp.nullable(),
    stale: z.boolean(),
});
export const createVariantResponse = z.object({
    variant: variantDto,
    generic_product: z.object({
        id: uuid,
        name: z.string(),
        slug: z.string(),
        category: z.string().nullable(),
    }),
    /**
     * False when the store could not be reached and the client's own fields were
     * used instead. The product is still added — it just has no EAN and no fresh
     * price, and saying so beats pretending (principle 6).
     */
    enriched: z.boolean(),
});
export const storeDto = z.object({
    id: uuid,
    code: storeCode,
    name: z.string(),
    supports_search: z.boolean(),
    supports_ean: z.boolean(),
    health: z.enum(ADAPTER_HEALTH),
    health_note: z.string().nullable(),
});
export const storesResponse = z.object({ stores: z.array(storeDto) });
//# sourceMappingURL=search.js.map